import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import PriorityOrderField from "@/components/PriorityOrderField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Fairness rule editor. Picks a metric, window and tolerance.

const METRICS = [
  ["weekend_count", "Weekend shifts"],
  ["night_count", "Night shifts"],
  ["holiday_count", "Holiday shifts"],
  ["overtime_hours", "Overtime hours"],
  ["category_count", "Assignments by type of work"],
  ["leave_approval_count", "Approved leave requests"],
];
const WINDOWS = [
  ["weekly", "Weekly"], ["monthly", "Monthly"],
  ["quarterly", "Quarterly"], ["rolling_90d", "Rolling 90 days"],
];

const EMPTY = {
  name: "", description: "", priority: 100, is_active: true,
  scope: "company", team_id: "", category_id: "",
  metric: "weekend_count", window: "monthly",
  max_deviation_pct: null, max_absolute_gap: null,
  weight: 1, effective_from: "", effective_to: "",
  per_location: false,
};

export default function FairnessRuleEditor({ open, onOpenChange, editing, categories, teams, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...EMPTY, ...editing,
        scope: editing.apply_per_team && editing.scope !== "category"
                 ? "each_team"
                 : (editing.scope || "company"),
        team_id: editing.team_id || "", category_id: editing.category_id || "",
        effective_from: editing.effective_from || "",
        effective_to: editing.effective_to || "",
        apply_per_team: !!editing.apply_per_team,
      });
    } else { setForm(EMPTY); }
  }, [open, editing]);

  const set = (p) => setForm((prev) => ({ ...prev, ...p }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.scope === "team" && !form.team_id) return toast.error("Team is required");
    if (form.scope === "category" && !form.category_id) return toast.error("Category is required");
    setBusy(true);
    try {
      const isEachTeam = form.scope === "each_team";
      const body = {
        name: form.name.trim(),
        description: form.description || null,
        priority: Number(form.priority) || 100,
        is_active: form.is_active,
        scope: isEachTeam ? "company" : form.scope,
        team_id: form.scope === "team" ? form.team_id : null,
        category_id: form.scope === "category" ? form.category_id : null,
        apply_per_team: isEachTeam || !!form.apply_per_team,
        metric: form.metric,
        window: form.window,
        max_deviation_pct: form.max_deviation_pct ?? null,
        max_absolute_gap: form.max_absolute_gap ?? null,
        weight: Number(form.weight) || 1,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        per_location: !!form.per_location,
      };
      if (editing) {
        await api.patch(`/fairness-rules/${editing.fairness_id}`, body);
        toast.success("Fairness policy updated");
      } else {
        await api.post("/fairness-rules", body);
        toast.success("Fairness policy created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" fullscreenOnMobile data-testid="fairness-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit fairness policy" : "New fairness policy"}</DialogTitle>
          <DialogDescription>
            Set fairness goals to spread weekend, night, holiday and overtime work
            evenly across your team over a chosen window.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="eyebrow">Name</Label>
              <Input required value={form.name} data-testid="fairness-name"
                onChange={(e) => set({ name: e.target.value })}
                className="h-11 rounded-lg mt-2" placeholder="e.g. Balance weekend rota" />
            </div>
            <div>
              <Label className="eyebrow">Scope</Label>
              <Select value={form.apply_per_team && form.scope !== "category" ? "each_team" : form.scope}
                       onValueChange={(v) => {
                         if (v === "each_team") set({ scope: "each_team", apply_per_team: true, team_id: "" });
                         else set({ scope: v, apply_per_team: false });
                       }}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="fairness-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company" data-testid="fairness-scope-opt-company">Company-wide</SelectItem>
                  <SelectItem value="team" data-testid="fairness-scope-opt-team">Specific team</SelectItem>
                  <SelectItem value="each_team" data-testid="fairness-scope-opt-each-team">Each team (one rule, evaluated per team)</SelectItem>
                  <SelectItem value="category" data-testid="fairness-scope-opt-category">Category</SelectItem>
                </SelectContent>
              </Select>
              {(form.scope === "each_team" || form.apply_per_team) && (
                <p className="text-xs text-[#4B5773] mt-2"
                   data-testid="fairness-scope-each-team-helper">
                  Each team is measured against its own team average instead
                  of the company-wide average.
                </p>
              )}
            </div>
            <div>
              <PriorityOrderField
                value={form.priority}
                onChange={(v) => set({ priority: v })}
                testid="fairness-priority" />
            </div>
            {form.scope === "team" && (
              <div className="col-span-2">
                <Label className="eyebrow">Team</Label>
                <Select value={form.team_id} onValueChange={(v) => set({ team_id: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="fairness-team"><SelectValue placeholder="Pick a team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => (
                      <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === "category" && (
              <div className="col-span-2">
                <Label className="eyebrow">Category</Label>
                <Select value={form.category_id} onValueChange={(v) => set({ category_id: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="fairness-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                  <SelectContent>
                    {/* Phase 2C - fairness rules target Work only.
                        Equalising Break/Away across employees is
                        non-sensical (Away time is non-productive,
                        not something to balance fairly). */}
                    {categories
                      .filter((c) => c.is_active !== false
                                      && c.kind === "work")
                      .map((c) => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="eyebrow">What to balance</Label>
              <Select value={form.metric} onValueChange={(v) => set({ metric: v })}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="fairness-metric"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METRICS.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Time window to measure across</Label>
              <Select value={form.window} onValueChange={(v) => set({ window: v })}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="fairness-window"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WINDOWS.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Maximum % above the team average</Label>
              <Input type="number" min="0" max="1000" data-testid="fairness-max-dev"
                value={form.max_deviation_pct ?? ""}
                onChange={(e) => set({
                  max_deviation_pct: e.target.value === "" ? null : Number(e.target.value),
                })}
                className="h-11 rounded-lg mt-2" placeholder="e.g. 20" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                How much more than the team average one person can have before NextRota flags it. For example, 20 means 20% above average.
              </p>
            </div>
            <div>
              <Label className="eyebrow">Maximum gap between people</Label>
              <Input type="number" min="0" data-testid="fairness-max-abs"
                value={form.max_absolute_gap ?? ""}
                onChange={(e) => set({
                  max_absolute_gap: e.target.value === "" ? null : Number(e.target.value),
                })}
                className="h-11 rounded-lg mt-2" placeholder="e.g. 3" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                The largest acceptable difference between the busiest and quietest person. For example, 3 means up to 3 extra weekends.
              </p>
            </div>
            <div>
              <Label className="eyebrow">Importance (1–5)</Label>
              <Input type="number" min="1" max="5" data-testid="fairness-weight"
                value={form.weight}
                onChange={(e) => set({ weight: Number(e.target.value) })}
                className="h-11 rounded-lg mt-2" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                How strongly NextRota should weight this rule when several rules apply.
              </p>
            </div>
            <div>
              <Label className="eyebrow">Status</Label>
              <Select value={form.is_active ? "active" : "archived"}
                onValueChange={(v) => set({ is_active: v === "active" })}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="fairness-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Effective from</Label>
              <Input type="date" value={form.effective_from} data-testid="fairness-eff-from"
                onChange={(e) => set({ effective_from: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Effective to</Label>
              <Input type="date" value={form.effective_to} data-testid="fairness-eff-to"
                onChange={(e) => set({ effective_to: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer"
                  data-testid="fairness-per-location-row">
            <input type="checkbox"
              checked={!!form.per_location}
              onChange={(e) => set({ per_location: e.target.checked })}
              className="mt-1 accent-[#2563EB]"
              data-testid="fairness-per-location-toggle" />
            <span>
              <span className="font-medium text-sm text-[#0B1A36]">
                Evaluate per location
              </span>
              <span className="block text-[11px] text-[#4B5773] mt-0.5">
                When enabled, fairness is measured independently at each work
                location (e.g. Saturday rotation balanced per site).
              </span>
            </span>
          </label>

          <div>
            <Label className="eyebrow">Description (optional)</Label>
            <Input value={form.description} data-testid="fairness-description"
              onChange={(e) => set({ description: e.target.value })}
              className="h-11 rounded-lg mt-2" />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="fairness-submit"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Saving…" : editing ? "Save changes" : "Create policy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
